import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GlobalSearchBar = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        performSearch();
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  const performSearch = async () => {
    setIsSearching(true);
    try {
      const response = await axios.get(
        `${API}/emr/patients/search?query=${encodeURIComponent(searchQuery)}`,
        { withCredentials: true }
      );
      setSearchResults(response.data);
      setShowResults(true);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectPatient = (patientId) => {
    setSearchQuery('');
    setShowResults(false);
    setSearchResults([]);
    navigate(`/emr/patient/${patientId}`);
  };

  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  return (
    <div className="relative w-full max-w-2xl" ref={searchRef}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
          <Search className="w-5 h-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search patients by name, DOB, or phone number..."
          className="w-full pl-12 pr-12 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
        />
        {searchQuery && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && (
        <div className="absolute z-50 w-full mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl max-h-96 overflow-y-auto">
          {isSearching ? (
            <div className="p-4 text-center text-gray-400">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-400 mx-auto"></div>
              <p className="mt-2">Searching...</p>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="p-4 text-center text-gray-400">
              <p>No patients found matching "{searchQuery}"</p>
              <p className="text-sm mt-1">Try searching by full name, date of birth (YYYY-MM-DD), or phone number</p>
            </div>
          ) : (
            <div className="py-2">
              <div className="px-4 py-2 text-xs text-gray-400 font-semibold uppercase">
                {searchResults.length} Patient{searchResults.length !== 1 ? 's' : ''} Found
              </div>
              {searchResults.map((patient) => (
                <button
                  key={patient.id}
                  onClick={() => handleSelectPatient(patient.id)}
                  className="w-full px-4 py-3 flex items-start space-x-3 hover:bg-gray-700 transition-colors text-left border-b border-gray-700 last:border-b-0"
                >
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-10 h-10 bg-yellow-400/20 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-yellow-400" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold">
                      {patient.first_name} {patient.last_name}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-400">
                      <span>DOB: {patient.dob}</span>
                      <span>•</span>
                      <span>Phone: {patient.cell_number}</span>
                    </div>
                    {patient.email && (
                      <p className="text-xs text-gray-500 mt-1">{patient.email}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <div className="text-yellow-400 text-sm">View →</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search Tips */}
      {searchQuery.length > 0 && searchQuery.length < 2 && (
        <div className="absolute z-40 w-full mt-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm text-blue-400">
          💡 Enter at least 2 characters to search
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;
